'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  category: 'education' | 'research' | 'achievement' | 'leadership';
}

interface TimelineSectionProps {
  className?: string;
}

const TimelineSection = ({ className = '' }: TimelineSectionProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const timelineEvents: TimelineEvent[] = [
    {
      year: '2008',
      title: 'Ph.D. in Environmental Science',
      description: 'Completed doctoral research on carbon sequestration in agricultural soils at IIT Delhi, laying the foundation for future innovations.',
      category: 'education',
    },
    {
      year: '2010',
      title: 'First Major Research Publication',
      description: 'Published groundbreaking paper on soil carbon dynamics in tropical ecosystems in Nature Climate Change.',
      category: 'research',
    },
    {
      year: '2013',
      title: 'International Recognition',
      description: 'Received Young Scientist Award from the International Society of Environmental Scientists.',
      category: 'achievement',
    },
    {
      year: '2015',
      title: 'Consulting for Fortune 500',
      description: 'Began advising multinational corporations on carbon credit strategies and ESG implementation.',
      category: 'leadership',
    },
    {
      year: '2018',
      title: 'UAE Market Entry',
      description: 'Expanded operations to United Arab Emirates, bringing sustainable solutions to Middle Eastern markets.',
      category: 'leadership',
    },
    {
      year: '2020',
      title: 'Founded GenCarbon ESG',
      description: 'Established GenCarbon to democratize carbon credit knowledge and provide accessible ESG solutions.',
      category: 'leadership',
    },
    {
      year: '2022',
      title: '50+ Published Research Papers',
      description: 'Reached milestone of 50 peer-reviewed publications in leading environmental science journals.',
      category: 'research',
    },
    {
      year: '2024',
      title: 'Industry Leadership Recognition',
      description: 'Named among Top 100 Sustainability Leaders in Asia by Environmental Business Journal.',
      category: 'achievement',
    },
  ];

  const categories = [
    { id: 'all', label: 'All Milestones', icon: 'Squares2X2Icon' },
    { id: 'education', label: 'Education', icon: 'AcademicCapIcon' },
    { id: 'research', label: 'Research', icon: 'BeakerIcon' },
    { id: 'achievement', label: 'Achievements', icon: 'TrophyIcon' },
    { id: 'leadership', label: 'Leadership', icon: 'UserGroupIcon' },
  ];

  const filteredEvents = selectedCategory === 'all' 
    ? timelineEvents 
    : timelineEvents.filter(event => event.category === selectedCategory);

  const getCategoryColor = (category: string) => {
    const colors = {
      education: 'bg-blue-500',
      research: 'bg-primary',
      achievement: 'bg-accent',
      leadership: 'bg-secondary',
    };
    return colors[category as keyof typeof colors] || 'bg-gray-500';
  };

  return (
    <section className={`bg-surface py-16 md:py-24 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-primary mb-4">
            Career Journey
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            A timeline of milestones, achievements, and contributions to environmental science and sustainability.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-primary text-primary-foreground shadow-natural'
                  : 'bg-card text-text-secondary hover:bg-muted border border-border'
              }`}
            >
              <Icon name={category.icon as any} size={18} />
              <span>{category.label}</span>
            </button>
          ))}
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-border transform md:-translate-x-1/2"></div>

          <div className="space-y-12">
            {filteredEvents.map((event, index) => (
              <div
                key={index}
                className={`relative flex items-start ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline dot */}
                <div className="absolute left-8 md:left-1/2 w-4 h-4 rounded-full bg-card border-4 border-primary transform -translate-x-1/2 md:translate-x-0"></div>

                {/* Content */}
                <div className={`ml-16 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                  <div className="bg-card p-6 rounded-xl border border-border hover:border-primary/30 hover:shadow-natural-lg transition-all duration-300">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className={`inline-block w-2 h-2 rounded-full ${getCategoryColor(event.category)}`}></span>
                      <span className="font-mono text-sm font-semibold text-accent">{event.year}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                      {event.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed">
                      {event.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;